import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import SurveyChartView from '../Charts/SurveyChartView';
import {
  CHART_WIDTH_TO_COL,
  type CustomSurveyReportItemDetailData,
} from '../consts';

interface Props {
  itemId: number;
  // When true, render the item name as a heading (the standalone shared view).
  showTitle?: boolean;
}

// Read-only render surface for one audience item of a report - the shared
// view. The item and every chart in its report come back from a single
// request: the server resolves this item's audience once, fetches the
// matching answers once, and computes every chart from that one shared
// fetch. This item's target_audience and view permissions are resolved and
// enforced entirely server-side. Used as the standalone shared-viewer page
// (CustomSurveyReportPage); the survey-admin builder has its own inline
// preview instead (CustomSurveyReportBuilder), scoped by the manager-only
// report-level chart-data endpoint.
const CustomSurveyReportView: FC<Props> = ({ itemId, showTitle = false }) => {
  const { formatMessage } = useIntl();

  const { data: item, status } =
    useConfirmApi<CustomSurveyReportItemDetailData>({
      method: 'GET',
      url: `custom-survey/report-items/${itemId}`,
    });

  const charts = useMemo(
    () => [...(item?.charts ?? [])].sort((a, b) => a.order - b.order),
    [item]
  );

  if (status === 'LOADING') {
    return <Loading />;
  }

  if (status === 'ERROR' || !item) {
    return (
      <EmptyState
        title={formatMessage({
          id: 'app.views.administration.custom_surveys.reports.view.error',
          defaultMessage: 'Could not load this report.',
        })}
      />
    );
  }

  return (
    <div className="d-flex flex-column gap-4">
      {showTitle && (
        <div className="d-flex flex-column gap-1">
          <h1 className="mb-0">{item.report_name}</h1>
          {/* The audience this copy of the report covers, e.g. a leader's
              org. */}
          {item.name && <span className="text-muted fs-3">{item.name}</span>}
        </div>
      )}

      {charts.length === 0 ? (
        <Card>
          <CardBody className="text-center text-muted">
            <FormattedMessage
              id="app.views.administration.custom_surveys.reports.view.empty"
              defaultMessage="This report has no charts yet."
            />
          </CardBody>
        </Card>
      ) : (
        <Row>
          {charts.map((chart) => (
            <Col
              key={chart.config_id}
              md={CHART_WIDTH_TO_COL[chart.width]}
              className="mb-4"
            >
              <Card className="h-100">
                <CardHeader>
                  <span className="fw-bold">
                    {chart.name || (
                      <FormattedMessage
                        id="app.views.administration.custom_surveys.charts.builder.untitled_chart"
                        defaultMessage="Chart"
                      />
                    )}
                  </span>
                </CardHeader>
                <CardBody className="d-flex flex-column justify-content-center">
                  {chart.data ? (
                    <SurveyChartView config={chart.config} data={chart.data} />
                  ) : (
                    <EmptyState
                      title={formatMessage({
                        id: 'app.views.administration.custom_surveys.charts.builder.chart_error',
                        defaultMessage: 'Could not load chart data.',
                      })}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default CustomSurveyReportView;
